import { AppChip, AppCopyText, AppPageHeader, AppSectionErrorBoundary, AppSpacer } from 'components/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { formatUTCDate } from 'utilities/DateUtils';

import { StaticTableWithFilters } from 'components/Tables/StaticTableWithFilters';
import { Checkbox, TableCell } from '@trackman/web-shared-components';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { useTranslation } from 'react-i18next';
import { appTableFiltersInstance, trackingDiagnosticsTableColumns } from './TrackingDiagnosticsTableColumns';
import { ITrackingDiagnostic } from 'types/models/TrackingDiagnosticsModel';
import AppBooleanChip from 'components/ui/Chips/AppBooleanChip';
import { getTrackingDiagnosticsSessionTypeColor, getTrackingDiagnosticsTypeColor } from 'utilities/SystemUtils';
import { AppButton } from 'components/form';
import { TrackingDiagnosticsManageDialog } from './components/dialogs/TrackingDiagnosticsManageDialog';
import TrackingDiagnosticsTableControl from './components/TrackingDiagnosticsTableControl';
import { SingleSorterType } from 'services/AppTableFilters';
import FormDiagnosticsPackageResponseModal from 'pages/DiagnosticsPackages/components/FormDiagnosticsPackageResponseModal';
import { FormDiagnosticsPackageDiaglog } from 'pages/DiagnosticsPackages/components/dialogs/FormDiagnosticsPackageDiaglog';
import { SelectDiagnosticsPackageDiaglogDiaglog } from 'pages/DiagnosticsPackages/components/dialogs/SelectDiagnosticsPackageDiaglog';

export const TrackingDiagnosticsPage = observer(() => {
    const { t } = useTranslation();

    const history = useHistory();
    const location = useLocation();

    const [visibleRows, setVisibleRows] = useState<ITrackingDiagnostic[]>([]);
    const [isControlDialogOpened, setIsControlDialogOpened] = useState<boolean>(false);
    const [isCreatePackageDialogOpened, setIsCreatePackageDialogOpened] = useState<boolean>(false);
    const [isSelectPackageDialogOpened, setIsSelectPackageDialogOpened] = useState<boolean>(false);

    const { trackingDiagnosticsStore, diagnosticsPackagesStore } = useStores();

    const autoSortColumn = "addedAtUtc";
    const DefaultAutoSortColumn: SingleSorterType = {
        id: autoSortColumn,
        type: "date",
        field: autoSortColumn,
        value: 'desc',
        active: true
    };

    useEffect(() => {
        async function fetchData() {
            await Promise.all([
                trackingDiagnosticsStore.getTrackingDiagnostics(),
                diagnosticsPackagesStore.getDiagnosticsPackages()
            ]);
        };
        fetchData();

        appTableFiltersInstance.clearSorters();
        appTableFiltersInstance.addSorter(DefaultAutoSortColumn);
    }, [diagnosticsPackagesStore, trackingDiagnosticsStore, trackingDiagnosticsStore.page, trackingDiagnosticsStore.take]);

    //Act based on SearchParams
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const selectedTrackingDiagnosticFromUrl = searchParams.get('selectedTrackingDiagnostic');

        if (selectedTrackingDiagnosticFromUrl && !isControlDialogOpened) {
            const trackingDiagnostic = trackingDiagnosticsStore.trackingDiagnostics.find(x => x.id == selectedTrackingDiagnosticFromUrl);
            if (trackingDiagnostic) {
                handleSelectingTrackingDiagnostic(trackingDiagnostic)
            }
        }

        const organizationNameFilter = searchParams.get('organizationName');

        if (organizationNameFilter) {
            appTableFiltersInstance.addFilter({
                id: 'organizationName',
                type: 'textfields',
                label: `${t('organizationName')}: ${organizationNameFilter}`,
                field: 'organizationName',
                value: organizationNameFilter,
            });
        }
    }, [trackingDiagnosticsStore.trackingDiagnostics]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const selectedTrackingDiagnosticFromUrl = searchParams.get('selectedTrackingDiagnostic');
        if (!selectedTrackingDiagnosticFromUrl && !isControlDialogOpened && trackingDiagnosticsStore.currentSelectedTrackingDiagnostic) {
            trackingDiagnosticsStore.setCurrentSelectedTrackingDiagnostic(null);
        }
    }, [isControlDialogOpened, trackingDiagnosticsStore.currentSelectedTrackingDiagnostic]);

    const rows = useMemo(() => trackingDiagnosticsStore.trackingDiagnostics, [trackingDiagnosticsStore.trackingDiagnostics]);
    const selectedRows = useMemo(() => trackingDiagnosticsStore.allSelectedTrackingDiagnosticIds, [trackingDiagnosticsStore.allSelectedTrackingDiagnosticIds]);

    const columns = useMemo(() =>
        trackingDiagnosticsTableColumns(trackingDiagnosticsStore.trackingDiagnosticsTypes, trackingDiagnosticsStore.sessionTypes),
        [trackingDiagnosticsStore.trackingDiagnosticsTypes, trackingDiagnosticsStore.sessionTypes]
    );

    const renderCell = useCallback(
        ({ cellValue, columnId, row }: { cellValue: any; columnHeader: string; columnId: string; row: any }) => {
            if (columnId === 'trackingDiagnosticDescription') {
                return (
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <p data-test={`systemID-cell-text`} className='mb-0 overflow-ellipsis' title={cellValue}>
                            {cellValue}
                        </p>
                    </div>
                );
            }

            if (columnId === 'trackingDiagnosticAddedAtUtc') {
                return (
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <p data-test={`systemID-cell-text`} className='mb-0 overflow-ellipsis' title={cellValue}>
                            {formatUTCDate(cellValue)}
                        </p>
                    </div>
                );
            }

            if (columnId === 'trackingDiagnosticId') {
                return (
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <p data-test={`systemID-cell-text`} className='mb-0 overflow-ellipsis' title={cellValue}>
                            {cellValue}
                        </p>
                        <AppCopyText className='ml-1 ' text={cellValue} />
                    </div>
                );
            }

            if (columnId === 'trackingDiagnosticHasFile') {
                return (
                    <AppBooleanChip value={cellValue as boolean | null} />
                )
            }

            if (columnId === 'trackingDiagnosticType') {
                return (
                    <AppChip<string> value={cellValue} getColor={() => getTrackingDiagnosticsTypeColor(cellValue)} />
                )
            }

            if (columnId === 'trackingDiagnosticSessionType') {
                return (
                    <AppChip<string> value={cellValue} getColor={() => getTrackingDiagnosticsSessionTypeColor(cellValue)} />
                )
            }

            if (columnId === 'trackingDiagnosticDescription') {
                <div className='d-flex justify-content-between align-items-center w-100'>
                    <p data-test={`systemID-cell-text`} className='mb-0 overflow-ellipsis' title={cellValue}>
                        {cellValue}
                    </p>
                </div>
            }

            if (columnId === 'actions') {
                const rowData: ITrackingDiagnostic = row as ITrackingDiagnostic;
                const id = rowData.id;
                return (
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <Checkbox
                            onChange={(e) => {
                                e?.stopPropagation();
                                const exists = selectedRows?.includes(id);
                                if (exists)
                                    trackingDiagnosticsStore.removeSelectedTrackingDiagnosticList(rowData);
                                else
                                    trackingDiagnosticsStore.addSelectedTrackingDiagnosticList(rowData);
                            }}
                            checked={selectedRows?.includes(id) ?? false}
                            disabled={!!!rowData.hasFile}
                            margin='3px'
                            label={""}
                        />
                        <AppButton
                            variant='secondary'
                            className='border-secondary'
                            onClick={(e) => {
                                e?.stopPropagation();
                                handleSelectingTrackingDiagnostic(rowData);
                            }}
                        >
                            {t('TrackingDiagnostics.Action.Manage')}
                        </AppButton>
                    </div>
                );
            }

            return cellValue || '-';
        },
        [selectedRows, trackingDiagnosticsStore]
    );

    //Manage Dialog
    const handleSelectingTrackingDiagnostic = useCallback((trackingDiagnostic: ITrackingDiagnostic) => {
        trackingDiagnosticsStore.setCurrentSelectedTrackingDiagnostic(trackingDiagnostic);
        setIsControlDialogOpened(true);
        updateSelectedTrackingDiagnosticsUrlParams(trackingDiagnostic, true);
    }, []);
    const handleClosingControlDialog = useCallback(() => {
        setIsControlDialogOpened(false);
        trackingDiagnosticsStore.setCurrentSelectedTrackingDiagnostic(null);
        updateSelectedTrackingDiagnosticsUrlParams(null, false);
    }, []);
    //------

    //Create Package Dialog
    const handleOpeningCreatePackageDialog = useCallback(() => {
        setIsCreatePackageDialogOpened(true);
    }, []);

    const handleClosingCreatePackageDialog = useCallback(() => {
        setIsCreatePackageDialogOpened(false);
    }, []);

    const handleOnCreatePackageDialog = useCallback(() => {
        setIsCreatePackageDialogOpened(false);
        trackingDiagnosticsStore.clearSelectedTrackingDiagnosticList();
    }, []);
    //------

    //Select Package Dialog
    const handleOpeningSelectPackageDialog = useCallback(() => {
        setIsSelectPackageDialogOpened(true);
    }, []);

    const handleClosingSelectPackageDialog = useCallback(() => {
        setIsSelectPackageDialogOpened(false);
    }, []);

    const handleOnSelectPackageDialog = useCallback(() => {
        setIsSelectPackageDialogOpened(false);
        trackingDiagnosticsStore.clearSelectedTrackingDiagnosticList();
    }, []);
    //------

    const updateSelectedTrackingDiagnosticsUrlParams = useCallback((trackingDiagnostic: ITrackingDiagnostic | null, isApplyingParam: boolean) => {
        const searchParams = new URLSearchParams(location.search);
        if (isApplyingParam && trackingDiagnostic) {
            searchParams.delete('selectedTrackingDiagnostic');
            searchParams.append('selectedTrackingDiagnostic', trackingDiagnostic.id);
        }
        else {
            searchParams.delete('selectedTrackingDiagnostic');
        }

        history.replace({
            pathname: location.pathname,
            search: searchParams.toString(),
        });
    }, []);

    const rowMapper = useCallback((cells: any[]) => {
        return (
            <>
                {cells.map(({ getCellProps, column, row, render, value }) => (
                    <TableCell {...getCellProps()} align={column.align} onClick={(e) => {
                        const cellPropsKey = getCellProps().key as string;
                        if (cellPropsKey.includes("actions"))
                            return;

                        e?.stopPropagation();
                        handleSelectingTrackingDiagnostic(row.original as ITrackingDiagnostic);
                    }}>
                        {renderCell({ cellValue: value, columnHeader: column.Header, columnId: column.id, row: row.original })}
                    </TableCell>
                ))}
            </>
        );
    }, [renderCell]);

    return (
        <AppSectionErrorBoundary>
            <div className='users-tables'>
                <AppPageHeader
                    title={`${t('TrackingDiagnostics.Title')}`}
                    backBtn={{ isVisible: true, onClick: () => history.push('/') }}
                >
                </AppPageHeader>

                <AppSpacer space='15px' />
                <TrackingDiagnosticsTableControl
                    visibleRows={visibleRows}
                    allSelectedTrackingDiagnosticIds={selectedRows}
                    onCreate={handleOpeningCreatePackageDialog}
                    onSelect={handleOpeningSelectPackageDialog}
                />
                <AppSpacer space='15px' />

                <StaticTableWithFilters<ITrackingDiagnostic>
                    appTableFiltersInstance={appTableFiltersInstance}
                    rowMapper={rowMapper}
                    rowsPerPage={trackingDiagnosticsStore.take}
                    columns={columns}
                    data={rows}
                    isLoading={trackingDiagnosticsStore.isLoadingTrackingDiagnostics && diagnosticsPackagesStore.isLoadingDiagnosticsPackages}
                    onVisibleRowsChange={(vr) => setVisibleRows(vr)}
                />

                <TrackingDiagnosticsManageDialog
                    isOpened={isControlDialogOpened}
                    onClose={handleClosingControlDialog}
                    trackingDiagnostic={trackingDiagnosticsStore.currentSelectedTrackingDiagnostic}
                />

                <FormDiagnosticsPackageDiaglog
                    isOpened={isCreatePackageDialogOpened}
                    onClose={handleClosingCreatePackageDialog}
                    onSuccessAfterClose={handleOnCreatePackageDialog}
                    diagnosticsPackage={null}
                    selectedEntries={selectedRows ?? []}
                />
                <SelectDiagnosticsPackageDiaglogDiaglog
                    isOpened={isSelectPackageDialogOpened}
                    onClose={handleClosingSelectPackageDialog}
                    onSuccessAfterClose={handleOnSelectPackageDialog}
                    selectedEntries={selectedRows ?? []}
                />
            </div>

            <FormDiagnosticsPackageResponseModal
                diagnositcsPackageUrl={diagnosticsPackagesStore.currentFormResponseUrl ?? '-'}
            />
        </AppSectionErrorBoundary>
    );
});

export default TrackingDiagnosticsPage;